import { v4 as uuidv4 } from 'uuid';

import apiInstance from './apiInstance';
import {
	KubeflexAvailableNetworksInfo,
	KubeflexConfigurationForCreateInfo,
	Sdk_ActivateIaasServiceAfterTestoutParams,
	Sdk_Fc_KvmKubernetesFlex,
	Sdk_Fc_KvmKubernetesFlexCalculation,
	Sdk_Fe_KvmKubernetesFlex,
	Sdk_Fe_KvmKubernetesFlexSpecialCost,
	Sdk_KubeflexList,
	Sdk_KvmKubernetesFlex_Wide,
	Sdk_KvmKubernetesFlexCalculation,
	Sdk_KvmKubernetesFlexFindFilter,
	Sdk_ServiceTestPeriodProlongInfo,
	Sdk_SubscriptionFindFilter,
	Sdk_SubscriptionList,
	Sdk_UserFindFilter,
	Sdk_UserList,
	Sdk_WLPartnerEndUserFinFilter,
	Sdk_WLPartnerEndUserList,
	WhoAmI,
} from './contracts';

interface ApiService {
	getUserInfo: () => Promise<WhoAmI>;
	getWLPartnerEndUsers: (filter: Sdk_WLPartnerEndUserFinFilter) => Promise<Sdk_WLPartnerEndUserList>;
	getUsersInfo: (filter: Sdk_UserFindFilter) => Promise<Sdk_UserList>;
	findKubeflex: (filter: Sdk_KvmKubernetesFlexFindFilter) => Promise<Sdk_KubeflexList>;
	getKubeflexById: (kubeflexId: string) => Promise<Sdk_KvmKubernetesFlex_Wide>;
	deleteKubeflex: (kubeflexId: string, freeUsedExtIp?: boolean) => Promise<void>;
	editKubeflex: (kubeflex: Sdk_Fe_KvmKubernetesFlex) => Promise<void>;
	voluntaryBlock: (kubeflexId: string) => Promise<void>;
	deleteVoluntaryBlock: (kubeflexId: string) => Promise<void>;
	repairToReadyLifecycleState: (kubeflexId: string) => Promise<void>;
	filterSubscriptions: (filter: Sdk_SubscriptionFindFilter) => Promise<Sdk_SubscriptionList>;
	getKubeflexConfigInfo: () => Promise<KubeflexConfigurationForCreateInfo>;
	activateAfterTestOut: (data: Sdk_ActivateIaasServiceAfterTestoutParams) => Promise<object>;
	prolongTestPeriod: (data: Sdk_ServiceTestPeriodProlongInfo) => Promise<object>;
	getKubeflexAvailableNetworks: ({
		kubernetesFlexId,
		kubernetesFlexTypeId,
		proxMoxClasterId,
		regionID,
	}: {
		kubernetesFlexId?: string;
		kubernetesFlexTypeId: string;
		proxMoxClasterId: string;
		regionID: string;
	}) => Promise<KubeflexAvailableNetworksInfo>;
	createKubeflex: (kubeflex: Sdk_Fc_KvmKubernetesFlex) => Promise<void>;
	getTemplate: (id: string) => Promise<{
		SystemDiskMinSize: number;
		RamMinSize: number;
	}>;
	downloadCalculation: (data: Sdk_Fc_KvmKubernetesFlexCalculation) => Promise<Sdk_KvmKubernetesFlexCalculation>;
	setSpecialCost: (data: Sdk_Fe_KvmKubernetesFlexSpecialCost) => Promise<void>;
	getToken: (data: { ClientApiId: string; SecretKey: string | null; ExpiryDate: string }) => Promise<{
		Token: string;
		ExpiryDate: string;
	}>;
}

export const apiService: ApiService = {
	getUserInfo: async () => {
		const trackingId = uuidv4();

		try {
			const response = await apiInstance.get<WhoAmI>(`token/whoami`, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	getWLPartnerEndUsers: async filter => {
		const trackingId = uuidv4();

		try {
			const response = await apiInstance.post<Sdk_WLPartnerEndUserList>(`wlpartnerenduser/find`, filter, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	getUsersInfo: async filter => {
		const trackingId = uuidv4();

		try {
			const response = await apiInstance.post<Sdk_UserList>(`user/find`, filter, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	findKubeflex: async filter => {
		const trackingId = uuidv4();

		try {
			const response = await apiInstance.post<Sdk_KubeflexList>(`kubeflex/find`, filter, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	deleteKubeflex: async (kubeflexId, freeUsedExtIp) => {
		const trackingId = uuidv4();

		try {
			await apiInstance.delete(`kubeflex?id=${kubeflexId}&freeUsedExtIp=${freeUsedExtIp}`, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});
		} catch (e) {
			return Promise.reject(e);
		}
	},
	getKubeflexConfigInfo: async () => {
		const trackingId = uuidv4();
		try {
			const response = await apiInstance.get<KubeflexConfigurationForCreateInfo>(
				`kubeflex/configurator/configurationinfo`,
				{
					headers: {
						'vendor-api-tracking-id': trackingId,
					},
				},
			);

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	getKubeflexAvailableNetworks: async params => {
		const trackingId = uuidv4();
		try {
			const response = await apiInstance.get<KubeflexAvailableNetworksInfo>(
				`kubeflex/configurator/availablenetworks`,
				{
					headers: {
						'vendor-api-tracking-id': trackingId,
					},
					params,
				},
			);

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	editKubeflex: async kubeflex => {
		const trackingId = uuidv4();

		try {
			await apiInstance.put(`kubeflex`, kubeflex, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});
		} catch (e) {
			return Promise.reject(e);
		}
	},
	voluntaryBlock: async kubeflexId => {
		const trackingId = uuidv4();

		try {
			await apiInstance.post(`kubeflex/voluntaryblock?id=${kubeflexId}`, null, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});
		} catch (e) {
			return Promise.reject(e);
		}
	},
	deleteVoluntaryBlock: async kubeflexId => {
		const trackingId = uuidv4();

		try {
			await apiInstance.delete(`kubeflex/voluntaryblock?id=${kubeflexId}`, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});
		} catch (e) {
			return Promise.reject(e);
		}
	},
	repairToReadyLifecycleState: async kubeflexId => {
		const trackingId = uuidv4();

		try {
			await apiInstance.post(
				`kubeflex/repairtoreadylifecyclestate`,
				{
					ID: kubeflexId,
				},
				{
					headers: {
						'vendor-api-tracking-id': trackingId,
					},
				},
			);
		} catch (e) {
			return Promise.reject(e);
		}
	},
	filterSubscriptions: async filter => {
		const trackingId = uuidv4();

		try {
			const response = await apiInstance.post<Sdk_SubscriptionList>(`subscription/find`, filter, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	createKubeflex: async data => {
		const trackingId = uuidv4();
		try {
			await apiInstance.post(`kubeflex`, data, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});
		} catch (e) {
			return Promise.reject(e);
		}
	},
	getTemplate: async id => {
		const trackingId = uuidv4();
		try {
			const response = await apiInstance.get<{ SystemDiskMinSize: number; RamMinSize: number }>(
				`kubeflex/configurator/template?vmTemplateId=${id}`,
				{
					headers: {
						'vendor-api-tracking-id': trackingId,
					},
				},
			);

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	getKubeflexById: async kubeflexId => {
		const trackingId = uuidv4();
		try {
			const response = await apiInstance.get<Sdk_KvmKubernetesFlex_Wide>(`kubeflex?id=${kubeflexId}`, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	downloadCalculation: async data => {
		const trackingId = uuidv4();
		try {
			const response = await apiInstance.post<Sdk_KvmKubernetesFlexCalculation>(
				`kubeflex/configurator/downloadсalculation`,
				data,
				{
					headers: {
						'vendor-api-tracking-id': trackingId,
					},
				},
			);

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	activateAfterTestOut: async data => {
		const trackingId = uuidv4();

		try {
			const response = await apiInstance.post<object>(`kubeflex/activateaftertestout`, data, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	prolongTestPeriod: async data => {
		const trackingId = uuidv4();

		try {
			const response = await apiInstance.post<object>(`kubeflex/prolongtestperiod`, data, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
	setSpecialCost: async data => {
		const trackingId = uuidv4();

		try {
			await apiInstance.post(`kubeflex/setspecialcost`, data, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});
		} catch (e) {
			return Promise.reject(e);
		}
	},
	getToken: async data => {
		const trackingId = uuidv4();
		try {
			const response = await apiInstance.post<{ Token: string; ExpiryDate: string }>(`token`, data, {
				headers: {
					'vendor-api-tracking-id': trackingId,
				},
			});

			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	},
};
